<script setup>
import { ref, computed, defineProps, reactive } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ZdhcService from '../../../services/Zdhc/ZdhcService';
import { associatedReferenceId } from '../../../utils/matchStatus';
/* Emits */
/* Props */
const props = defineProps({
    product:{required:true}
});
/* Data */
const {t} = useI18n();
const store = useStore();
const zdhcProduct = reactive({});
const matching_status = ref('loading');
const { showProduct } = ZdhcService();
/* Computed */
/**
 * E' l'identita' che conta con Gateway 2.0 — l'inventario viaggia con questa —
 * ed e' il motivo per cui ha preso il posto di PID e GUID: quei due non
 * partecipano piu' a niente, e mostrarli faceva sembrare che contassero ancora.
 */
const referenceId = computed( () => associatedReferenceId(props.product.incheck_reference));
const setReferenceDate = computed( () =>
{
    return props.product.incheck_reference && props.product.incheck_reference.reference_date
            ? moment(props.product.incheck_reference.reference_date).format('MMMM YYYY')
            : '-';
});
/* Methods */
/**
 * PID lookups no longer exist in the SP API: the associated product is
 * resolved through its product_reference_id (populated by new matches or
 * by the re-match migration). Legacy matches without a reference id show
 * as "no associated product" until re-matched.
 */
const init =  async () =>
{
    matching_status.value = 'loading';
    const reference = referenceId.value;
    if(reference)
    {
        const res = await showProduct(reference);
        if(res && !res.error && res.data)
        {
            Object.assign(zdhcProduct, {
                productName: res.data.product_name,
                formulatorName: res.data.formulator_name,
                conformance_level_name: res.data.conformance_level_name ?? null,
                ctz_level_name: res.data.ctz_level_name ?? null,
            });
        }
    }
    matching_status.value = 'fetched';
};
/* Hooks */
moment.locale(store.state.locale);
init();
</script>
<template>
    <!-- Partecipa alla catena del modale: il contenuto scorre e non viene
         tagliato quando lo schermo e' basso o il testo ingrandito. -->
    <div class="w-full flex-1 min-h-0 flex flex-col space-y-5">
        <template v-if="matching_status === 'loading'" >
            <div class="flex items-center justify-center h-72 overflow-y-auto overscroll-contain">
                <svg class="animate-spin w-12 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            </div>
        </template>
        <template v-else-if="matching_status === 'fetched'">
            <div class="flex flex-col space-y-5 flex-1 min-h-0 overflow-y-auto overscroll-contain text-sm">
                <!-- Il nome del prodotto locale apre il riquadro: senza, aprendo
                     il modale non si capisce di quale prodotto si stia guardando
                     il match. -->
                <p>
                    {{ t('productTable.cols.name', store.state.locale) }}:
                    <span class="font-semibold">
                        {{ props.product.name }}
                    </span>
                </p>
                <p v-if="props.product.formulator">
                    {{ t('productTable.cols.formulator', store.state.locale) }}:
                    <span class="font-semibold">
                        {{ props.product.formulator.name }}
                    </span>
                </p>
                <p>
                    Reference Id:
                    <span class="font-semibold">
                        {{ referenceId ?? '-' }}
                    </span>
                </p>
                <p>
                    {{ t('productTable.cols.quantity', store.state.locale) }}:
                    <span class="font-semibold">
                        {{ props.product.incheck_reference.quantity / 100 }} Kg
                    </span>
                </p>
                <p>
                    {{ t('productTable.cols.reference_date', store.state.locale) }}:
                    <span class="font-semibold">
                        {{ setReferenceDate }}
                    </span>
                </p>
                <div class="flex flex-col space-y-3">
                    <span>{{ t('matchModal.associated_product', store.state.locale) }}:</span>
                    <span v-if="!Object.keys(zdhcProduct).length" class="font-semibold">
                        {{ t('matchModal.no_associated_product', store.state.locale) }}
                    </span>
                    <div v-else class="flex flex-col space-y-5 bg-gray-100 dark:bg-gray-800 rounded-md p-2">
                        <!-- Name -->
                        <p>
                            {{ t('productTable.cols.name', store.state.locale) }}:
                            <span class="font-semibold capitalize">
                                {{ zdhcProduct.productName }}
                            </span>
                        </p>
                        <!-- Formulator -->
                        <div class="flex flex-col space-y-3">
                            <p>
                                {{ t('product.create.formulator', store.state.locale) }}:
                                <span class="font-semibold">
                                    {{ zdhcProduct.formulatorName }}
                                </span>
                            </p>
                        </div>
                        <!-- Conformance (flat model of the new SP API) -->
                        <p>
                            {{ t('matchModal.certifications', store.state.locale) }}:
                            <span v-if="!zdhcProduct.conformance_level_name && !zdhcProduct.ctz_level_name" class="font-semibold">
                               {{ t('matchModal.no_certifications', store.state.locale) }}
                            </span>
                        </p>
                        <div v-if="zdhcProduct.conformance_level_name || zdhcProduct.ctz_level_name" class="felx flex-col space-y-3">
                            <div class="felx flex-col space-y-3 bg-gray-200 dark:bg-gray-700 p-2 rounded-md">
                                <p v-if="zdhcProduct.conformance_level_name">
                                    ZDHC MRSL:
                                    <span class="font-semibold">
                                    {{ zdhcProduct.conformance_level_name }}
                                    </span>
                                </p>
                                <p v-if="zdhcProduct.ctz_level_name">
                                    CtZ:
                                    <span class="font-semibold">
                                    {{ zdhcProduct.ctz_level_name }}
                                    </span>
                                </p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </template>
    </div>
</template>